/**
 * PWA Install Prompt Component
 * Shows a banner encouraging users to install the app on their device
 * ONLY displays on the Home page ("/")
 */
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { X, Download, Smartphone } from "lucide-react";

export function PWAInstallPrompt() {
  const location = useLocation();
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  // Only show on Home page
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    // Don't show on any page except Home
    if (!isHomePage) {
      setShowPrompt(false);
      return;
    }

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if it's iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    // Check if prompt was dismissed recently (24 hours)
    const dismissedAt = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissedAt) {
      const hoursAgo = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60);
      if (hoursAgo < 24) {
        return;
      }
    }

    // Listen for the beforeinstallprompt event (Chrome, Edge, etc.)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // For iOS, show the prompt after a delay (since there's no native prompt)
    if (iOS && !window.matchMedia('(display-mode: standalone)').matches) {
      setTimeout(() => setShowPrompt(true), 3000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [isHomePage]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      // Chrome/Edge - trigger native install prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('[PWA] User accepted install');
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  if (!showPrompt || isInstalled || !isHomePage) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-in slide-in-from-bottom duration-300">
      <div className="bg-[#0A0A0A] border border-[#23F7DD]/30 rounded-2xl p-4 shadow-2xl shadow-[#23F7DD]/10">
        <div className="flex items-start gap-4">
          {/* App Icon */}
          <div className="w-14 h-14 bg-[#23F7DD] rounded-xl flex items-center justify-center flex-shrink-0">
            <span className="text-[#050505] font-bold text-2xl font-['Unbounded']">X</span>
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-white font-bold text-lg">Install AllStillas</h3>
                <p className="text-[#94A3B8] text-sm mt-1">
                  {isIOS 
                    ? "Add to your home screen for quick access" 
                    : "Install our app for a better experience"
                  }
                </p>
              </div>
              <button 
                onClick={handleDismiss}
                className="text-[#94A3B8] hover:text-white p-1 -mr-1 -mt-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Action */}
            <div className="mt-3">
              {isIOS ? (
                <div className="text-xs text-[#94A3B8] space-y-1">
                  <p className="flex items-center gap-2">
                    <span className="text-lg">1.</span> Tap the <span className="text-[#23F7DD]">Share</span> button
                  </p>
                  <p className="flex items-center gap-2">
                    <span className="text-lg">2.</span> Select <span className="text-[#23F7DD]">"Add to Home Screen"</span>
                  </p>
                </div>
              ) : (
                <Button 
                  onClick={handleInstall}
                  className="w-full bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Install App
                </Button>
              )}
            </div>
          </div>
        </div>
        
        {/* Features preview */}
        <div className="mt-4 pt-3 border-t border-white/10">
          <div className="flex items-center justify-around text-xs text-[#64748B]">
            <span className="flex items-center gap-1">
              <Smartphone className="w-3 h-3" />
              Works offline
            </span>
            <span>•</span>
            <span>Fast access</span>
            <span>•</span>
            <span>Notifications</span>
          </div>
        </div>
      </div>
    </div>
  );
}
